import { useMutation, useQuery } from '@apollo/client';
import { useCallback } from 'react';
import {
    CREATE_LESSON_SHARES,
    CreateLessonSharesResponse,
    CreateLessonSharesVariables,
    DELETE_LESSON_SHARES,
    DeleteLessonShareResponse,
    DeleteLessonShareVariables,
    GET_LESSON,
    GetLessonResponse,
    GetLessonVariables,
} from './gql';

export const useLesson = (id?: string) => {
    const { data, loading, refetch } = useQuery<GetLessonResponse, GetLessonVariables>(GET_LESSON, {
        variables: {
            where: {
                id: id ?? '',
            },
        },
        fetchPolicy: 'no-cache',
        skip: !id,
    });
    const [createLessonShares] = useMutation<CreateLessonSharesResponse, CreateLessonSharesVariables>(CREATE_LESSON_SHARES);
    const [deleteLessonShare] = useMutation<DeleteLessonShareResponse, DeleteLessonShareVariables>(DELETE_LESSON_SHARES);

    const share = useCallback(
        async (emails: string[]) => {
            if (!id) {
                return;
            }
            await createLessonShares({
                variables: {
                    data: emails.map((email) => ({
                        email,
                        lesson: {
                            connect: { id },
                        },
                        user: {
                            connect: { email },
                        },
                    })),
                },
            });

            const resp = await refetch();

            return resp.data?.lesson;
        },
        [createLessonShares, id, refetch],
    );

    const unshare = useCallback(
        async (ids: string[]) => {
            const result = await deleteLessonShare({
                variables: {
                    where: ids.map((share) => ({ id: share })),
                },
            });

            if (id) {
                await refetch();
            }

            return result.data?.deleteLessonShares ?? [];
        },
        [deleteLessonShare, id, refetch],
    );

    return {
        lesson: data?.lesson,
        loading,
        share,
        unshare,
    };
};
